import { API_BASE_URL } from '../../../../../config';
import { VariousCopyDocCodes } from '../../../../../catalog/variousCopyDocCodes';
import { FORM_CODE_FF008 } from './careerUpConfirmationTypes';
import type {
  CareerUpConfirmationFormValues,
  CareerUpConfirmationUploadMeta,
  CopyUploadMeta,
} from './careerUpConfirmationTypes';

export const DONE_STEP_ID = '__done__';

export type CareerUpConfirmationProgressPayload = {
  values: CareerUpConfirmationFormValues;
  currentStepId: string | null;
  uploads: CareerUpConfirmationUploadMeta;
};

export function buildVariousCopyFileUrl(
  email: string,
  invitationId: string,
  docCode: string
): string | null {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim()) return null;
  const params = new URLSearchParams({
    email: email.trim(),
    invitationId: invitationId.trim(),
    docCode,
  });
  return `${API_BASE_URL}/api/chat-flow/documents/file?${params.toString()}`;
}

/** @deprecated use buildVariousCopyFileUrl */
export function buildVc008FileUrl(email: string, invitationId: string): string | null {
  return buildVariousCopyFileUrl(email, invitationId, VariousCopyDocCodes.CAREER_UP_RELATED);
}

function parseCopyMeta(data: {
  vc008HasUpload?: boolean;
  vc008FileName?: string;
  vc008UploadedAt?: string;
  vc010HasUpload?: boolean;
  vc010FileName?: string;
  vc010UploadedAt?: string;
}): CareerUpConfirmationUploadMeta {
  return {
    vc008: {
      hasUpload: Boolean(data.vc008HasUpload),
      fileName: data.vc008FileName ?? undefined,
      uploadedAt: data.vc008UploadedAt ?? undefined,
    },
    vc010: {
      hasUpload: Boolean(data.vc010HasUpload),
      fileName: data.vc010FileName ?? undefined,
      uploadedAt: data.vc010UploadedAt ?? undefined,
    },
  };
}

function mapValuesFromApi(data: {
  hasCareerUpCard?: string;
  careerUpCardUploaded?: string;
  uploadConfirmed?: string;
  driversLicenseUploaded?: string;
  cardPassword?: string;
}): CareerUpConfirmationFormValues {
  const values: CareerUpConfirmationFormValues = {};
  if (data.hasCareerUpCard === 'yes' || data.hasCareerUpCard === 'no') {
    values.hasCareerUpCard = data.hasCareerUpCard;
  }
  const cardUploaded = data.careerUpCardUploaded ?? data.uploadConfirmed;
  if (cardUploaded === 'yes' || cardUploaded === 'no') {
    values.careerUpCardUploaded = cardUploaded;
  }
  if (data.driversLicenseUploaded === 'yes' || data.driversLicenseUploaded === 'no') {
    values.driversLicenseUploaded = data.driversLicenseUploaded;
  }
  if (data.cardPassword?.trim()) {
    values.cardPassword = data.cardPassword.trim();
  }
  return values;
}

export async function fetchCareerUpConfirmationProgress(
  email: string,
  invitationId: string
): Promise<CareerUpConfirmationProgressPayload | null> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim()) return null;
  const params = new URLSearchParams({
    email: email.trim(),
    invitationId: invitationId.trim(),
    formCode: FORM_CODE_FF008,
  });
  const r = await fetch(`${API_BASE_URL}/api/career-up-confirmation/progress?${params.toString()}`);
  if (!r.ok) return null;
  const data = await r.json();
  return {
    values: mapValuesFromApi(data),
    currentStepId: data.currentStepId ?? null,
    uploads: parseCopyMeta(data),
  };
}

export async function upsertCareerUpConfirmationProgress(params: {
  email: string;
  invitationId: string;
  values: CareerUpConfirmationFormValues;
  currentStepId: string | null;
}): Promise<CareerUpConfirmationProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim() || !params.invitationId?.trim()) return null;
  try {
    const body: Record<string, string | null> = {
      email: params.email.trim(),
      invitationId: params.invitationId.trim(),
      formCode: FORM_CODE_FF008,
      currentStepId: params.currentStepId,
    };
    if (params.values.hasCareerUpCard) body.hasCareerUpCard = params.values.hasCareerUpCard;
    if (params.values.careerUpCardUploaded) {
      body.careerUpCardUploaded = params.values.careerUpCardUploaded;
    }
    if (params.values.driversLicenseUploaded) {
      body.driversLicenseUploaded = params.values.driversLicenseUploaded;
    }
    if (params.values.cardPassword?.trim()) body.cardPassword = params.values.cardPassword.trim();

    const r = await fetch(`${API_BASE_URL}/api/career-up-confirmation/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      console.warn('Career-up confirmation save failed', r.status);
      return null;
    }
    const data = await r.json();
    const values: CareerUpConfirmationFormValues = { ...params.values, ...mapValuesFromApi(data) };
    return {
      values,
      currentStepId: data.currentStepId ?? params.currentStepId,
      uploads: parseCopyMeta(data),
    };
  } catch (e) {
    console.warn('Career-up confirmation save failed', e);
    return null;
  }
}
